import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import TextArea from "@/components/TextArea/TextArea";
import Form from "@/components/Form/Form";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { clearAssetComment, updateAssetComment } from "@/api/Equipment";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import styles from "./AssetDetails.module.scss";

type CommentRowProps = {
  assetId: string;
  comment: string | null | undefined;
};

export const CommentRow = ({ assetId, comment }: CommentRowProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { confirm } = useDialog();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const [isEditing, setIsEditing] = useState(false);

  const trimmedComment = comment?.trim() ? comment : null;

  const { register, handleSubmit, reset } = useForm<{ comment: string }>({
    defaultValues: { comment: trimmedComment ?? "" },
  });

  const { mutate: saveComment, isPending: isSaving } = useMutation({
    mutationFn: updateAssetComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setIsEditing(false);
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att spara kommentaren.`} ${deducedError(error)}`,
      );
    },
  });

  const { mutate: removeComment, isPending: isRemoving } = useMutation({
    mutationFn: clearAssetComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      reset({ comment: "" });
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort kommentaren.`} ${deducedError(error)}`,
      );
    },
  });

  const cancelEdit = () => {
    reset({ comment: trimmedComment ?? "" });
    setIsEditing(false);
  };

  const handleClear = async () => {
    const confirmed = await confirm({
      title: t`Ta bort kommentar`,
      message: t`Är du säker på att du vill ta bort kommentaren?`,
    });
    if (confirmed) {
      removeComment({ assetId });
    }
  };

  return (
    <>
      <DescriptionList.Term>
        <Trans>Kommentar</Trans>
      </DescriptionList.Term>
      <DescriptionList.Description>
        <div className={styles.fieldWithAction}>
          <Text element="span">{trimmedComment ?? "–"}</Text>
          <div className={styles.actions}>
            {isEditing ? (
              <PlainButton size="small" onClick={cancelEdit}>
                <Trans>Avbryt</Trans>
              </PlainButton>
            ) : (
              <>
                <PlainButton
                  size="small"
                  aria-label={
                    trimmedComment ? t`Ändra kommentar` : t`Lägg till kommentar`
                  }
                  onClick={() => setIsEditing(true)}
                >
                  {trimmedComment ? (
                    <Trans>Ändra</Trans>
                  ) : (
                    <Trans>Lägg till</Trans>
                  )}
                </PlainButton>
                {trimmedComment ? (
                  <PlainButton
                    size="small"
                    onClick={handleClear}
                    disabled={isRemoving}
                  >
                    <Trans>Ta bort</Trans>
                  </PlainButton>
                ) : null}
              </>
            )}
          </div>
          {isEditing ? (
            <div className={styles.editContainer}>
              <Form
                onSubmit={handleSubmit(({ comment: nextComment }) =>
                  saveComment({ assetId, comment: nextComment.trim() }),
                )}
              >
                <Form.Row>
                  <TextArea
                    label={t`Kommentar`}
                    showOptionalLabel
                    {...register("comment")}
                  />
                </Form.Row>
                <FilledButton type="submit" disabled={isSaving}>
                  <Trans>Spara</Trans>
                </FilledButton>
              </Form>
            </div>
          ) : null}
        </div>
      </DescriptionList.Description>
    </>
  );
};
